// assets/product-grid-actions.js

class ProductGridActions {
  constructor() {
    this.wishlistEndpoint = this.detectWishlistEndpoint();
    this.guestWishlistKey = 'guest_wishlist_items_v3';
    this.maxGuestItems = 50;
    this.initEventListeners();
    this.setupObservers();
    this.checkLoginStatus();
  }

  detectWishlistEndpoint() {
    if (typeof SwymPageEvents !== 'undefined') return '/swym-relay';
    if (typeof WishlistKing !== 'undefined') return '/apps/wishlist-king/add';
    if (typeof Growave !== 'undefined') return '/apps/growave/wishlist/add';
    return '/apps/wishlist/add';
  }

  checkLoginStatus() {
    this.isLoggedIn = typeof Shopify !== 'undefined' && !!Shopify.customer_id;
  }

  initEventListeners() {
    document.addEventListener('click', (event) => {
      const wishlistBtn = event.target.closest('.add-wishlist');
      if (wishlistBtn) {
        event.preventDefault();
        this.handleWishlistAction(wishlistBtn);
      }
      
      const compareBtn = event.target.closest('.add-compare');
      if (compareBtn) {
        event.preventDefault();
        this.handleCompareAction(compareBtn);
      }

      const removeWishlistBtn = event.target.closest('.remove-wishlist');
      if (removeWishlistBtn) {
        event.preventDefault();
        this.handleRemoveWishlistAction(removeWishlistBtn);
      }
    });

    if (typeof Shopify !== 'undefined') {
      document.addEventListener('customer:login', () => {
        this.checkLoginStatus();
        this.syncGuestWishlist();
      });
      document.addEventListener('customer:logout', () => this.checkLoginStatus());
    }
  }

  setupObservers() {
    if (typeof Shopify !== 'undefined') {
      document.addEventListener('shopify:section:load', () => this.initEventListeners());
    }
    if (typeof Turbo !== 'undefined') {
      document.addEventListener('turbo:load', () => this.initEventListeners());
    }
  }

  async handleWishlistAction(button) {
    const productHandle = button.dataset.productHandle;
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId || productId;
    const cardWrapper = button.closest('.card-wrapper');
    const productImage = cardWrapper?.querySelector('img')?.src || '';
    const productTitle = button.dataset.productTitle || '';
    
    button.classList.add('loading');
    button.disabled = true;

    this.checkLoginStatus();

    if (!this.isLoggedIn) {
      this.handleGuestWishlist(button, {
        id: productId,
        variantId,
        handle: productHandle,
        title: productTitle,
        image: productImage,
        price: button.dataset.productPrice || ''
      });
      return;
    }

    if (typeof SwymPageEvents !== 'undefined') {
      try {
        SwymPageEvents.addToWishList({
          epi: productId,
          du: window.location.href,
          empi: productId,
          dt: document.title,
          img: productImage
        });
        this.handleWishlistSuccess(button);
        return;
      } catch (e) {
        console.warn('Swym JS API failed, falling back to AJAX', e);
      }
    }

    try {
      const response = await fetch(this.wishlistEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: productId,
          variant_id: variantId,
          handle: productHandle,
          customer_id: Shopify.customer_id || null
        })
      });

      if (!response.ok) throw new Error('Wishlist request failed');
      this.handleWishlistSuccess(button);
    } catch (error) {
      this.handleWishlistError(button, error);
    }
  }

  handleGuestWishlist(button, productData) {
    try {
      let wishlistItems = JSON.parse(localStorage.getItem(this.guestWishlistKey)) || [];
      
      // Exact match check for product variant
      const exists = wishlistItems.some(item => 
        item.id === productData.id && 
        item.variantId === productData.variantId
      );

      if (exists) {
        button.classList.remove('loading');
        button.disabled = false;
        this.showToast('This item is already in your wishlist', 'info');
        return;
      }

      if (wishlistItems.length >= this.maxGuestItems) {
        wishlistItems.shift();
        this.showToast('Wishlist full. Oldest item removed.', 'info');
      }

      wishlistItems.push({
        ...productData,
        addedAt: new Date().toISOString()
      });

      localStorage.setItem(this.guestWishlistKey, JSON.stringify(wishlistItems));
      this.handleWishlistSuccess(button);
      this.showToast('Added to wishlist successfully!', 'success');
      this.updateWishlistCount(wishlistItems.length);
    } catch (error) {
      this.handleWishlistError(button, error);
    }
  }

  handleRemoveWishlistAction(button) {
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId || productId;
    
    button.classList.add('loading');
    button.disabled = true;

    this.checkLoginStatus();

    if (!this.isLoggedIn) {
      try {
        let wishlistItems = JSON.parse(localStorage.getItem(this.guestWishlistKey)) || [];
        const initialLength = wishlistItems.length;
        
        wishlistItems = wishlistItems.filter(item => 
          !(item.id === productId && item.variantId === variantId)
        );

        if (wishlistItems.length < initialLength) {
          localStorage.setItem(this.guestWishlistKey, JSON.stringify(wishlistItems));
          this.showToast('Removed from wishlist', 'success');
          this.updateWishlistCount(wishlistItems.length);
          button.closest('.wishlist-item')?.remove();
        } else {
          this.showToast('Item not found in wishlist', 'info');
        }
      } catch (error) {
        this.showToast('Error removing item', 'error');
      } finally {
        button.classList.remove('loading');
        button.disabled = false;
      }
      return;
    }

    // Logged-in removal logic would go here
    button.classList.remove('loading');
    button.disabled = false;
  }

  async syncGuestWishlist() {
    if (!this.isLoggedIn || typeof Shopify === 'undefined') return;

    try {
      const guestItems = JSON.parse(localStorage.getItem(this.guestWishlistKey)) || [];
      if (guestItems.length === 0) return;

      for (const item of guestItems) {
        try {
          if (typeof SwymPageEvents !== 'undefined') {
            SwymPageEvents.addToWishList({
              epi: item.id,
              du: `${window.location.origin}/products/${item.handle}`,
              empi: item.id,
              dt: item.title,
              img: item.image
            });
          } else {
            await fetch(this.wishlistEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                id: item.id,
                variant_id: item.variantId,
                handle: item.handle,
                customer_id: Shopify.customer_id
              })
            });
          }
        } catch (e) {
          console.error(`Failed to sync item ${item.id}:`, e);
        }
      }

      localStorage.removeItem(this.guestWishlistKey);
      this.showToast('Your wishlist items have been saved', 'success');
      this.updateWishlistCount();
    } catch (error) {
      console.error('Error syncing guest wishlist:', error);
    }
  }

  handleWishlistSuccess(button) {
    button.classList.remove('loading');
    button.classList.add('added');
    button.disabled = false;
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
    this.updateWishlistCount();
  }

  handleWishlistError(button, error) {
    button.classList.remove('loading');
    button.disabled = false;
    this.showToast(
      error.message.includes('404') ? 
      'Wishlist service unavailable' : 
      'Error processing wishlist', 
      'error'
    );
  }

  updateWishlistCount() {
    let count = 0;
    
    if (this.isLoggedIn && typeof SwymPageEvents !== 'undefined' && SwymPageEvents.getWishlistCount) {
      count = SwymPageEvents.getWishlistCount();
    } else {
      try {
        const guestItems = JSON.parse(localStorage.getItem(this.guestWishlistKey)) || [];
        count = guestItems.length;
      } catch (e) {
        console.error('Error reading guest wishlist:', e);
      }
    }
    
    document.querySelectorAll('.wishlist-count').forEach(el => {
      el.textContent = count > 0 ? count : '';
      el.style.display = count > 0 ? 'inline-block' : 'none';
    });
    
    return count;
  }

  handleCompareAction(button) {
    const productHandle = button.dataset.productHandle;
    
    button.classList.add('loading');
    button.disabled = true;
    
    let compareItems = JSON.parse(localStorage.getItem('compareItems')) || [];
    
    if (!compareItems.includes(productHandle)) {
      compareItems.push(productHandle);
      localStorage.setItem('compareItems', JSON.stringify(compareItems));
      button.classList.remove('loading');
      button.classList.add('added');
      button.disabled = false;
      this.showToast('Added to compare');
      document.dispatchEvent(new CustomEvent('compare:updated', {
        detail: { count: compareItems.length }
      }));
    } else {
      button.classList.remove('loading');
      button.disabled = false;
      this.showToast('Already in compare', 'info');
    }
  }

  showToast(message, type = 'success') {
    if (typeof Shopify !== 'undefined' && Shopify.toast) {
      Shopify.toast(message, { duration: 3000, class: type });
      return;
    }

    const toast = document.createElement('div');
    toast.className = `toast-message toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('fade-out');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
}

// Initialize only once
if (!window.productGridActions) {
  window.productGridActions = new ProductGridActions();
  
  const initWishlistCount = () => {
    window.productGridActions.updateWishlistCount();
  };
  
  if (document.readyState !== 'loading') {
    initWishlistCount();
  } else {
    document.addEventListener('DOMContentLoaded', initWishlistCount);
  }
  
  if (typeof Turbo !== 'undefined') {
    document.addEventListener('turbo:load', () => {
      if (!window.productGridActions) {
        window.productGridActions = new ProductGridActions();
      }
      window.productGridActions.updateWishlistCount();
    });
  }
}